import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { BookOpen, GraduationCap } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { stripHtml } from '../../lib/htmlText';

export default function CourseCatalog() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([
        api.get('/courses?published_only=true'),
        api.get('/enrollments/my')
      ]);
      setCourses(coursesRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrolling(courseId);
    try {
      await api.post('/enrollments', { user_id: '', course_id: courseId });
      toast.success('Successfully enrolled in course!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(null);
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const catOf = (c) => c.category_name || c.category || '';
  const filteredCourses = courses.filter(c => categoryFilter === 'all' || catOf(c) === categoryFilter);

  const categories = [...new Set(courses.map(catOf).filter(Boolean))].sort();

  const openCourse = (course) => {
    if (!isEnrolled(course.id)) {
      toast.info('Enroll in this course first to open it');
      return;
    }
    navigate(`/learn/course/${course.id}`);
  };

  const enrollCourse = (course) => {
    if (isEnrolled(course.id)) {
      toast.info('Already enrolled');
      return;
    }
    handleEnroll(course.id);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Page data-testid="course-catalog">
        <DataGrid
          title="Course catalog"
          rows={filteredCourses}
          loading={loading}
          onRefresh={fetchData}
          advancedSearch={(
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {categoryFilter !== 'all' && (
                <Button variant="ghost" size="sm" className="h-9" onClick={() => setCategoryFilter('all')}>Clear</Button>
              )}
            </div>
          )}
          activeFilterCount={categoryFilter === 'all' ? 0 : 1}
          columns={[
            { key: 'title', header: 'Course', className: 'font-medium' },
            { key: 'instructor_name', header: 'Instructor', width: 160, accessor: (c) => c.instructor_name || '', className: 'text-muted-foreground' },
            { key: 'category', header: 'Category', width: 160, accessor: catOf },
            {
              key: 'enrolled', header: 'Enrolled', width: 120,
              accessor: (c) => (isEnrolled(c.id) ? 'Enrolled' : 'Available'),
              render: (c) => (isEnrolled(c.id)
                ? <StatusBadge value="enrolled" label="Enrolled" tone="success" />
                : <StatusBadge value="available" label="Available" tone="info" />),
            },
            { key: 'enrollment_count', header: 'Enrollments', width: 120, align: 'right', accessor: (c) => c.enrollment_count ?? 0 },
            {
              key: 'enable_certificate', header: 'Certificate', width: 120,
              accessor: (c) => (c.enable_certificate ? 'Enabled' : 'Disabled'),
              render: (c) => <StatusBadge value={c.enable_certificate ? 'enabled' : 'disabled'} />,
            },
            { key: 'description', header: 'Description', hidden: true, accessor: (c) => stripHtml(c.description, 0), className: 'text-muted-foreground max-w-md truncate' },
          ]}
          rowTone={(c) => (isEnrolled(c.id) ? 'success' : null)}
          onRowDoubleClick={(c) => (isEnrolled(c.id) ? openCourse(c) : enrollCourse(c))}
          selectionActions={[
            { key: 'enroll', label: 'Enroll', icon: GraduationCap, onClick: ([c]) => enrollCourse(c), disabled: !!enrolling },
            { key: 'open', label: 'Open', icon: BookOpen, onClick: ([c]) => openCourse(c) },
          ]}
          emptyMessage="No courses found"
        />
      </Page>
    </DashboardLayout>
  );
}
